import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Buildings, PaperPlaneTilt, CheckCircle, House } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";
import { trackLead } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  property_address: "",
  bedrooms: "",
  bathrooms: "",
  square_footage: "",
  has_pool: false,
  has_hot_tub: false,
  has_sauna: false,
  has_other: false,
  other_amenity: "",
  is_short_term_rental: false,
  listing_url: "",
  management_status: "",
  timeframe: "",
  notes: "",
};

export default function ManagePage() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.property_address) {
      toast.error("Please fill in your name, phone, email and property address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/management-inquiries`, form);
      trackLead({ form: "property_management" });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-b border-emerald/25 py-3 text-emerald placeholder:text-emerald-soft/50 focus:border-sunset focus:outline-none transition-colors duration-300 w-full";

  if (done) {
    return (
      <main className="min-h-[80vh] pt-32 md:pt-40 pb-24 flex items-center">
        <div className="max-w-[820px] mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="management-thankyou"
          >
            <span className="inline-flex w-20 h-20 rounded-full bg-sage/15 items-center justify-center mb-8">
              <CheckCircle size={44} weight="fill" className="text-sage" />
            </span>
            <p className="overline text-sunset mb-5">Thank you</p>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-6">
              Thanks for your interest in Double Rainbow Suites co-hosting.
            </h1>
            <p className="text-lg text-emerald-soft font-light max-w-xl mx-auto mb-10">
              We've received your details and a member of our team will be in contact with you shortly.
            </p>
            <Link to="/" data-testid="thankyou-home-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald">
              <House size={18} weight="fill" /> Back to home
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Airbnb Co-Hosting & Property Management — Fort Lauderdale & Wilton Manors | Double Rainbow Suites"
        description="Own a short-term rental in Fort Lauderdale or Wilton Manors? Double Rainbow Suites co-hosts alongside you — listing optimization, guest messaging, dynamic pricing and 5-star turnovers. A flexible alternative to traditional property management, with you staying the host of record."
        canonical={`${SITE}/property-management`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Short-term rental co-hosting (vacation rental property management)",
            provider: { "@id": `${SITE}/#business` },
            areaServed: [
              { "@type": "City", name: "Fort Lauderdale" },
              { "@type": "City", name: "Wilton Manors" },
            ],
            url: `${SITE}/property-management`,
            description:
              "Hospitality-focused Airbnb co-hosting and vacation rental management support for owners in Fort Lauderdale and Wilton Manors, Florida — owners stay the host of record.",
          },
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Co-Hosting", path: "/property-management" },
          ]),
        ]}
      />
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex items-center gap-3 mb-5">
            <Buildings size={26} weight="duotone" className="text-sunset" />
            <p className="overline text-sunset">Co-hosting for owners</p>
          </div>
          <h1 className="font-display text-4xl md:text-7xl leading-[0.9] tracking-tight">
            Co-host your property with us.
          </h1>
          <p className="text-lg text-emerald-soft max-w-2xl mt-6 font-light">
            We know what it takes to build a successful short-term rental because we've done it ourselves. At Double Rainbow Suites, we don't just manage properties — we build, operate, and grow short-term rental businesses. From creating a property guests want to book, to optimizing listings, pricing, guest communication, operations, and the overall guest experience, we bring hands-on experience from running our own successful STRs. Now, we're offering that experience to other property owners.
          </p>
        </motion.div>

        {/* What co-hosting covers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 border-t border-emerald/12 pt-10"
          data-testid="cohost-services"
        >
          <p className="overline text-sunset mb-6">What we handle as your co-host</p>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
            {[
              "Listing optimization — compelling copy, photo order, pricing and calendar settings",
              "Guest messaging & vetting — fast, friendly and around the clock",
              "Dynamic pricing tuned to seasonality and the local event calendar",
              "Turnover coordination — trusted local cleaners, restocks and quality checks",
              "Design-led staging advice from a professional interior-design background",
              "5-star guest experience — local guidebooks, recommendations and on-trip support",
            ].map((s) => (
              <li key={s} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                <CheckCircle size={18} weight="fill" className="text-sunset shrink-0 mt-1" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Co-hosting vs property manager */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-14 border-t border-emerald/12 pt-10"
          data-testid="cohost-vs-pm"
        >
          <p className="overline text-sunset mb-5">Co-hosting vs. a traditional property manager</p>
          <p className="text-emerald-soft font-light leading-relaxed max-w-3xl">
            A traditional property manager takes over — often holding the listing, the guest relationship and the payouts under their name, on their contract. As your co-host we work alongside you instead: the listing stays yours, payouts go straight to you, and you can be as hands-on or hands-off as you like. You get professional, hospitality-grade operations without giving up ownership of your business.
          </p>
        </motion.div>

        {/* Florida compliance note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-14 border-t border-emerald/12 pt-10"
          data-testid="cohost-florida"
        >
          <p className="overline text-sunset mb-5">Florida hosting, done right</p>
          <p className="text-emerald-soft font-light leading-relaxed max-w-3xl">
            Florida has no single "property management" license — but vacation rentals do need the right registrations, and managing someone else's rental for a fee can be a licensed real-estate activity here. Our co-hosting model keeps it simple: you remain the host of record and hold your DBPR vacation-rental license and local registrations, while we run the hospitality side and help you navigate the paperwork for your address.
          </p>
          <p className="text-emerald-soft/70 text-sm font-light mt-4 max-w-3xl">
            We're hosts, not lawyers — we'll happily point you to the right DBPR, county and city resources for your address.
          </p>
        </motion.div>

        <motion.form
          data-testid="management-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-12 border border-emerald/10 rounded-2xl p-8 md:p-10 bg-sand-2 space-y-9"
        >
          {/* Contact */}
          <div>
            <p className="overline text-emerald-soft mb-5">Your details</p>
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label className="overline text-emerald-soft">Name *</label>
                <input data-testid="mgmt-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div>
                <label className="overline text-emerald-soft">Phone number *</label>
                <input data-testid="mgmt-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-emerald-soft">Email address *</label>
                <input data-testid="mgmt-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
            </div>
          </div>

          {/* Property */}
          <div>
            <p className="overline text-emerald-soft mb-5">Your property</p>
            <div className="grid sm:grid-cols-2 gap-7">
              <div className="sm:col-span-2">
                <label className="overline text-emerald-soft">Property address *</label>
                <input data-testid="mgmt-address" className={inputCls} value={form.property_address} onChange={set("property_address")} placeholder="Street, city, state, ZIP" />
              </div>
              <div>
                <label className="overline text-emerald-soft">Bedrooms</label>
                <input data-testid="mgmt-bedrooms" type="number" min="0" className={inputCls} value={form.bedrooms} onChange={set("bedrooms")} placeholder="e.g. 2" />
              </div>
              <div>
                <label className="overline text-emerald-soft">Bathrooms</label>
                <input data-testid="mgmt-bathrooms" type="number" min="0" step="0.5" className={inputCls} value={form.bathrooms} onChange={set("bathrooms")} placeholder="e.g. 2" />
              </div>
              <div>
                <label className="overline text-emerald-soft">Square footage</label>
                <input data-testid="mgmt-sqft" type="number" min="0" className={inputCls} value={form.square_footage} onChange={set("square_footage")} placeholder="e.g. 1200" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-emerald-soft">Features</label>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="mgmt-pool-label">
                    <input data-testid="mgmt-pool" type="checkbox" checked={form.has_pool} onChange={toggle("has_pool")} className="w-5 h-5 accent-sunset" />
                    <span className="text-emerald">Pool</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="mgmt-hottub-label">
                    <input data-testid="mgmt-hottub" type="checkbox" checked={form.has_hot_tub} onChange={toggle("has_hot_tub")} className="w-5 h-5 accent-sunset" />
                    <span className="text-emerald">Hot tub</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="mgmt-sauna-label">
                    <input data-testid="mgmt-sauna" type="checkbox" checked={form.has_sauna} onChange={toggle("has_sauna")} className="w-5 h-5 accent-sunset" />
                    <span className="text-emerald">Sauna</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="mgmt-other-label">
                    <input data-testid="mgmt-other" type="checkbox" checked={form.has_other} onChange={toggle("has_other")} className="w-5 h-5 accent-sunset" />
                    <span className="text-emerald">Other</span>
                  </label>
                  {form.has_other && (
                    <input
                      data-testid="mgmt-other-text"
                      className="bg-transparent border-b border-emerald/25 py-2 text-emerald placeholder:text-emerald-soft/50 focus:border-sunset focus:outline-none transition-colors duration-300 flex-1 min-w-[180px]"
                      value={form.other_amenity}
                      onChange={set("other_amenity")}
                      placeholder="Tell us what…"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="overline text-emerald-soft mb-5">A little more</p>
            <label className="flex items-start gap-3 cursor-pointer mb-7" data-testid="mgmt-str-label">
              <input data-testid="mgmt-str" type="checkbox" checked={form.is_short_term_rental} onChange={toggle("is_short_term_rental")} className="w-5 h-5 mt-0.5 accent-sunset" />
              <span className="text-emerald">Is this property already being used as a short-term rental?</span>
            </label>
            <div className="mb-7" data-testid="mgmt-listing-wrap">
              <label className="overline text-emerald-soft">Current listing link — if available (Airbnb / VRBO / Booking.com)</label>
              <input data-testid="mgmt-listing-url" type="url" className={inputCls} value={form.listing_url} onChange={set("listing_url")} placeholder="https://airbnb.com/rooms/…" />
            </div>
            <div>
              <p className="text-emerald mb-3">Is your place self-hosted right now, or does it have a property manager or co-host?</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <label className={`flex-1 flex items-center gap-3 rounded-xl border px-5 py-4 cursor-pointer transition-colors duration-300 ${form.management_status === "self" ? "border-sunset bg-sunset/5" : "border-emerald/15 hover:border-emerald/30"}`} data-testid="mgmt-status-self">
                  <input type="radio" name="management_status" value="self" checked={form.management_status === "self"} onChange={set("management_status")} className="w-5 h-5 accent-sunset" />
                  <span className="text-emerald">Self-hosted</span>
                </label>
                <label className={`flex-1 flex items-center gap-3 rounded-xl border px-5 py-4 cursor-pointer transition-colors duration-300 ${form.management_status === "manager" ? "border-sunset bg-sunset/5" : "border-emerald/15 hover:border-emerald/30"}`} data-testid="mgmt-status-manager">
                  <input type="radio" name="management_status" value="manager" checked={form.management_status === "manager"} onChange={set("management_status")} className="w-5 h-5 accent-sunset" />
                  <span className="text-emerald">Has a manager or co-host</span>
                </label>
              </div>
            </div>
            <div className="mt-7">
              <p className="text-emerald mb-3">Time-frame</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { value: "1-2 months", label: "1–2 months" },
                  { value: "3-5 months", label: "3–5 months" },
                  { value: "6+ months", label: "6+ months" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    data-testid={`mgmt-timeframe-${opt.value.replace(/[^0-9a-z]+/gi, "-")}`}
                    className={`flex items-center gap-3 rounded-xl border px-5 py-4 cursor-pointer transition-colors duration-300 ${form.timeframe === opt.value ? "border-sunset bg-sunset/5" : "border-emerald/15 hover:border-emerald/30"}`}
                  >
                    <input type="checkbox" checked={form.timeframe === opt.value} onChange={() => setForm((f) => ({ ...f, timeframe: f.timeframe === opt.value ? "" : opt.value }))} className="w-5 h-5 accent-sunset" />
                    <span className="text-emerald">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-7">
              <label className="overline text-emerald-soft">Anything else? (optional)</label>
              <textarea data-testid="mgmt-notes" rows={4} className={`${inputCls} resize-none`} value={form.notes} onChange={set("notes")} placeholder="Tell us anything else about your property or goals…" />
            </div>
          </div>

          <button
            data-testid="mgmt-submit"
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald disabled:opacity-60"
          >
            {loading ? "Sending…" : "Submit"}
            <PaperPlaneTilt size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    </main>
  );
}
